import { getTqsProxyBase } from '@/lib/tqs-proxy'
import { getAccessToken } from '@/services/auth-service'

/**
 * Base URL of the TQS BFF proxy (``{backend}/api/v1/tqs``), normalized and
 * https-defaulted. Shared by all Communicator 2.0 hooks so the proxy-URL
 * logic lives in exactly one place.
 */
export function buildTqsProxyUrl(): string {
  return getTqsProxyBase()
}

/**
 * Auth headers for the BFF proxy: Azure-AD bearer when a token is present

 * Single source of truth; previously copy-pasted into every hook.
 */
export function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra }
  const token = getAccessToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

/**
 * Read the most useful error message out of a failed BFF/TQS response.
 *
 * The backends answer errors as JSON ``{detail: string | {message: string}}``
 * (FastAPI convention; the BFF mode-gate nests a ``message``). A 422 is the
 * odd one out: FastAPI's validation errors arrive as an ARRAY of
 * ``{loc, msg, type}`` (tqs#806 / cdb-pdb#26 — a too-long chat message hit
 * exactly this), which matched neither branch and degraded to a bare
 * "HTTP 422". Falls back to ``HTTP <status>`` when the body is not JSON or
 * carries no detail.
 */
export async function readErrorDetail(res: Response): Promise<string> {
  const fallback = `HTTP ${res.status}${res.statusText ? `: ${res.statusText}` : ''}`
  try {
    const body = await res.json()
    return formatErrorDetail(body?.detail) ?? fallback
  } catch {
    /* non-JSON error body — keep the status fallback */
  }
  return fallback
}

/**
 * Turn a FastAPI ``detail`` into a readable line, or null if it carries nothing.
 *
 * Split out of :func:`readErrorDetail` so the body is parsed ONCE and the three
 * shapes stay interpreted in one place — :func:`toTqsHttpError` needs both the
 * string and the structured fields, and reading the response twice to get them
 * would be a bug waiting to happen (cdb_tqs#899).
 */
export function formatErrorDetail(detail: unknown): string | null {
  if (typeof detail === 'string') return detail.trim() || null
  if (Array.isArray(detail)) {
    // Name the offending field so "message" / "knowledge_areas" is visible,
    // not just the complaint. ``loc`` is ["body", "message", ...] — the
    // "body"/"query" prefix is noise to a user, so drop it.
    const lines = detail
      .map((e) => {
        const msg = typeof e?.msg === 'string' ? e.msg.trim() : ''
        if (!msg) return ''
        const path = Array.isArray(e?.loc)
          ? e.loc.filter((p: unknown) => typeof p === 'string' && p !== 'body' && p !== 'query')
          : []
        return path.length > 0 ? `${path.join('.')}: ${msg}` : msg
      })
      .filter(Boolean)
    if (lines.length > 0) return lines.join('; ')
  }
  if (
    detail &&
    typeof detail === 'object' &&
    typeof (detail as { message?: unknown }).message === 'string'
  ) {
    return (detail as { message: string }).message.trim() || null
  }
  return null
}

/**
 * Fetch against the TQS proxy with the shared auth contract (bearer header +
 * ``credentials: 'include'``). ``path`` is relative to the
 * proxy base, e.g. ``/agent/v2/sessions``. Throws an Error carrying the
 * backend's ``detail`` message on a non-OK response.
 */
export async function tqsProxyFetch(path: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(`${buildTqsProxyUrl()}${path}`, {
    credentials: 'include',
    ...init,
    headers: authHeaders(init?.headers as Record<string, string> | undefined),
  })
  if (!res.ok) {
    // A `TqsHttpError` IS an `Error` with the same `message` this used to
    // throw, so callers reading only `.message` are unaffected (cdb_tqs#899).
    throw await toTqsHttpError(res)
  }
  return res
}

/**
 * A failed TQS response with its machine-readable half kept intact
 * (cdb_tqs#899).
 *
 * `message` is exactly what `readErrorDetail` produced before — the backend's
 * German sentence, or an `HTTP <status>` fallback. New is `code`: on paths that
 * set one, that is what a caller localizes on, because the server cannot know
 * which language this app is running in.
 *
 * `fields` holds the numbers the sentence quotes (`limit`, `max_mb`, …). They
 * arrive as their own values so a translation can place them rather than
 * parsing them back out of German.
 *
 * `hasServerMessage` says whether `message` is something a server actually
 * wrote, or the `HTTP <status>` line this file synthesizes when the body is
 * absent or carries no detail (#697). Callers that fall back to `message` need
 * that distinction: a gateway 504 with an empty body is not a sentence, and
 * showing it puts a status code in front of the user. Recorded here rather than
 * matched on the string later, because a server sentence is free to start with
 * anything — including "HTTP".
 */
export class TqsHttpError extends Error {
  readonly status: number
  readonly code?: string
  readonly fields: Record<string, unknown>
  readonly hasServerMessage: boolean

  constructor(
    message: string,
    status: number,
    code?: string,
    fields: Record<string, unknown> = {},
    hasServerMessage = true,
  ) {
    super(message)
    this.name = 'TqsHttpError'
    this.status = status
    this.code = code
    this.fields = fields
    this.hasServerMessage = hasServerMessage
  }
}

/** Build a `TqsHttpError` from a failed response. Reads the body once. */
export async function toTqsHttpError(res: Response): Promise<TqsHttpError> {
  const fallback = `HTTP ${res.status}${res.statusText ? `: ${res.statusText}` : ''}`
  let detail: unknown
  try {
    detail = (await res.json())?.detail
  } catch {
    // No JSON body at all — an infrastructure hop answering for the origin.
    return new TqsHttpError(fallback, res.status, undefined, {}, false)
  }
  const serverText = formatErrorDetail(detail)
  const message = serverText ?? fallback
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const { code, ...rest } = detail as Record<string, unknown>
    delete rest.message
    return new TqsHttpError(
      message,
      res.status,
      typeof code === 'string' && code ? code : undefined,
      rest,
      serverText !== null,
    )
  }
  return new TqsHttpError(message, res.status, undefined, {}, serverText !== null)
}
